import { Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { merge } from 'lodash-es';
import React from 'react';

export interface StripeProviderProps {
  /**
   * Resolves to a Stripe object which is the entrypoint for the rest of the Stripe.js SDK
   * @see https://docs.stripe.com/js/initializing
   */
  stripePromise?: Promise<Stripe | null>;
  customerSessionClientSecret?: string;
  children: React.ReactNode;
  availablePaymentMethods?: string[];
  elementsOptions?: StripeElementsOptions;
}

/**
 * Wrapper for the Stripe Elements provider that enables children to use Stripe components
 * and access the Stripe object.
 *
 * @see https://docs.stripe.com/stripe-js/react#elements-provider
 */
export default function StripeProvider({
  stripePromise,
  customerSessionClientSecret,
  children,
  availablePaymentMethods = ['card'],
  elementsOptions,
}: StripeProviderProps) {
  // If there are no secrets to be initialized, make this provider transparent
  if (!stripePromise || !customerSessionClientSecret) {
    return children;
  }

  const options = {
    mode: 'setup',
    currency: 'usd',
    customerSessionClientSecret,
    paymentMethodTypes: availablePaymentMethods,
    paymentMethodOptions: {
      us_bank_account: {
        verification_method: 'instant',
      },
    },
  };
  const mergedOptions = merge(options, elementsOptions);

  return (
    <Elements stripe={stripePromise} options={mergedOptions}>
      {children}
    </Elements>
  );
}
